/**
 * ProgressStepCard — Renders pipeline/progress steps inline in the chat.
 * Used by the Authoring Agent, Crosstabs Builder, or any agent that emits
 * authoring_step / authoring_step_update SSE events.
 */

import React from 'react'
import {Box, Typography} from '@mui/material'
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  RadioButtonChecked as RunningIcon,
} from '@mui/icons-material'
import type {ProgressStepData} from './types'

const STATUS_CONFIG = {
  running:  {icon: RunningIcon,  color: '#5b7fa6', spin: true},
  complete: {icon: CheckIcon,    color: '#2e8b6e', spin: false},
  warning:  {icon: WarningIcon,  color: '#c4873b', spin: false},
  failed:   {icon: ErrorIcon,    color: '#c45048', spin: false},
  pending:  {icon: RunningIcon,  color: '#b0aea5', spin: false},
} as const

interface Props {
  steps: ProgressStepData[]
}

const ProgressStepCard: React.FC<Props> = ({steps}) => {
  if (!steps.length) return null

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 0.75,
      py: 1, px: 0.5,
    }}>
      {steps.map(step => {
        const config = STATUS_CONFIG[step.status] ?? STATUS_CONFIG.pending
        const Icon = config.icon

        return (
          <Box key={step.stepId} sx={{
            display: 'flex', flexDirection: 'column', gap: 0.5,
            py: 0.75, px: 1.5,
            borderRadius: '8px',
            bgcolor: step.status === 'running' ? 'rgba(91,127,166,0.06)' : 'transparent',
            transition: 'background-color 0.2s',
          }}>
            {/* Step header */}
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <Icon sx={{
                fontSize: 16,
                color: config.color,
                ...(config.spin && {
                  animation: 'spin 1.5s linear infinite',
                  '@keyframes spin': {
                    '0%': {transform: 'rotate(0deg)'},
                    '100%': {transform: 'rotate(360deg)'},
                  },
                }),
              }} />
              <Typography sx={{
                fontSize: '0.82rem',
                fontWeight: 600,
                color: config.color,
                flex: 1,
              }}>
                {step.name}
              </Typography>
              {step.timing && (
                <Typography sx={{fontSize: '0.7rem', color: 'text.disabled'}}>
                  {step.timing}
                </Typography>
              )}
            </Box>

            {/* Description */}
            {step.description && (
              <Typography sx={{fontSize: '0.75rem', color: 'text.secondary', pl: 3.5}}>
                {step.description}
              </Typography>
            )}

            {/* Details */}
            {step.details && Object.keys(step.details).length > 0 && (
              <Typography sx={{fontSize: '0.7rem', color: 'text.disabled', pl: 3.5}}>
                {Object.entries(step.details).map(([k, v]) => `${k}: ${v}`).join(' · ')}
              </Typography>
            )}

            {/* Substeps */}
            {step.substeps && step.substeps.length > 0 && (
              <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.25, pl: 3.5, mt: 0.25}}>
                {step.substeps.map((sub, i) => {
                  const subIcon = sub.status === 'complete' ? '✓' : sub.status === 'warning' ? '⚠' : '·'
                  const subColor = sub.status === 'complete' ? '#2e8b6e' : sub.status === 'warning' ? '#c4873b' : 'text.disabled'
                  return (
                    <Typography key={i} sx={{fontSize: '0.7rem', color: subColor}}>
                      {subIcon} {sub.name} {sub.result && <span style={{color: '#b0aea5'}}>{sub.result}</span>}
                    </Typography>
                  )
                })}
              </Box>
            )}
          </Box>
        )
      })}
    </Box>
  )
}

export default ProgressStepCard
